import useSwitchState from '@anm/hooks/useSwitchState';
import { useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { projectSelectors } from '../../../store/modules/project';
import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import DuplicateStreamModal from '../../my-projects/ProjectPlayerBlock/DuplicateStreamModal';
import { useEditBigVideoAlert } from '../../my-projects/ProjectPlayerBlock/EditBigVideoAlert';
import getEditLink from '../../my-projects/ProjectPlayerBlock/helpers/getEditLink';
import isProjectBig from '../../my-projects/ProjectPlayerBlock/helpers/isProjectBig';

import { Wrapper } from './Wrapper';

type EditButtonProps = {
  projectId: string;
};

type EditButtonBodyProps = {
  onClick?(): void;
  disabled?: boolean;
};

const EditButtonBody: FC<EditButtonBodyProps> = props => (
  <Wrapper
    {...props}
    size="full-width"
    variant="light-blue"
    buttonType="container"
    icon="images/dashboard/edit_video_icon.svg"
  >
    <span>Edit video</span>
  </Wrapper>
);

const useEditLink = (projectId: string, onCanNotOpen: Function) => {
  const project = useSelector(projectSelectors.selectCurrentProject);
  if (!project) return [];

  const { version, duration } = project;
  const renderEditLink = getEditLink(!isProjectBig({ version, duration }), projectId, () => onCanNotOpen(true));

  const canEdit = !project.extra.streams;

  return [canEdit, renderEditLink] as const;
};

const EditButton: FC<EditButtonProps> = ({ projectId }) => {
  const dispatch = useDispatch();
  const [EditBigVideoAlert, openEditBigVideoAlert] = useEditBigVideoAlert();
  const [canEdit, renderEditLink] = useEditLink(projectId, openEditBigVideoAlert);
  const folderId = useSelector(projectSelectors.selectCurrentProjectFolderId);

  const [isOpenEditStreamModal, openEditStreamModal, closeEditStreamModal] = useSwitchState();

  const handleDuplicate = () =>
    dispatch(videoFolderActions.duplicateAndOpenEditorProject({ folderId, projectId, isOpenNewProject: true }));
  const duplicateProjectEntity = useSelector(videoFolderSelectors.selectDuplicateProject);
  const isDuplicatePending = useSelector(state => videoFolderSelectors.selectProjectDuplicateStatus(state, projectId));

  useEffect(() => {
    duplicateProjectEntity.params && !isDuplicatePending && closeEditStreamModal();
  }, [isDuplicatePending]);

  if (!renderEditLink) return null;

  return (
    <>
      {EditBigVideoAlert}
      {isOpenEditStreamModal && (
        <DuplicateStreamModal
          isPending={isDuplicatePending}
          onModalClose={closeEditStreamModal}
          onRightButtonClick={handleDuplicate}
        />
      )}

      {canEdit ? renderEditLink(<EditButtonBody />) : <EditButtonBody onClick={openEditStreamModal} />}
    </>
  );
};

export default EditButton;
